import Vue from 'vue';
import Vuex from 'vuex';
import ProgressBar from '@/shared/progress-bar/progress-bar';
import Router from 'vue-router';
import auth from '@/modules/auth/auth-module';
import shared from '@/shared/shared-module';
import home from '@/modules/home/home-module';
import layout from '@/modules/layout/layout-module';
import notifications from '@/modules/notifications/notifications-module';
import realEstateRegister from '@/modules/real-estate-register/real-estate-register-module';
import { savePcuelAccountId, savePcuelOrganizationId } from '@/shared/services/pcuel-account.service';

const modules = {
    shared,
    home,
    auth,
    layout,
    notifications,
    realEstateRegister,
};

const exists = (el) => !!el;

function setupComponentsFiltersDirectivesAndMixins() {
    Object.keys(modules)
        .map((key) => modules[key].components)
        .filter(exists)
        .forEach((components) => {
            components.forEach((component) => {
                Vue.component(component.name, component);
            });
        });

    Object.keys(modules)
        .map((key) => modules[key].directives)
        .filter(exists)
        .forEach((directives) => {
            directives.forEach((directive) => {
                Vue.directive(directive.name, directive.implementation);
            });
        });

    Object.keys(modules)
        .map((key) => modules[key].mixins)
        .filter(exists)
        .forEach((mixins) => {
            mixins.forEach((mixin) => {
                Vue.mixin(mixin);
            });
        });
}

const buildStores = () => {
    const output = {};

    Object.keys(modules)
        .filter((key) => !!modules[key].store)
        .forEach((key) => {
            output[key] = modules[key].store;
        });
    return output;
};

let store = null;

const storeAsync = () => {
    if (!store) {
        store = new Vuex.Store({ modules: buildStores() });
    }

    return store;
};

// FIX for NavigationDuplicated: Avoided redundant navigation to current location
const originalPush = Router.prototype.push;
Router.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => err)
};

const domainRouterFiles = require.context('./modules', true, /routes.js/)
const domainRoutes = domainRouterFiles.keys().reduce((carry, item) => {
  return [...carry, ...domainRouterFiles(item).default]
}, []);

const routes = [
    {
        path: '/',
        redirect: '/home'
    },
    ...domainRoutes,
    {
        path: '*',
        redirect: '/not-found',
    },
];

let router = null;
let previousRoute = null;

const routerAsync = () => {
    if (!router) {
        // Capture accountId from the initial URL before router redirects strip query params
        const urlParams = new URLSearchParams(window.location.search);
        const initialAccountId = urlParams.get('accountId');
        if (initialAccountId) {
            savePcuelAccountId(initialAccountId);
        }
        const initialOrganizationId = urlParams.get('organizationId');
        if (initialOrganizationId) {
            savePcuelOrganizationId(initialOrganizationId);
        }

        router = new Router({
            mode: 'history',
            routes,
            scrollBehavior() {
                return { x: 0, y: 0 };
            },
        });

        router.beforeEach(async (to, from, next) => {
            previousRoute = from;

            const accountId = to.query?.accountId;
            if (accountId) {
                savePcuelAccountId(accountId);
            }
            const organizationId = to.query?.organizationId;
            if (organizationId) {
                savePcuelOrganizationId(organizationId);
            }

            if (to.name) {
                ProgressBar.start();
            }

            // Ensure store is initialized
            if (!store) {
                store = storeAsync();
            }

            await store.dispatch('shared/fetchPublicSettings');

            // Check if citizen maintenance mode is active
            const appConfig = store.getters['shared/publicAppConfig'] || {};
            const isCitizenMaintenance = appConfig.CITIZEN_MAINTENANCE === true;
            const publicRoutes = window.VUE_APP_PUBLIC_ROUTES?.split(',') || [];
            const publicAppUrl = new URL(window.VUE_APP_EXTERNAL_APP_URL);
            const isPublicRoute = publicRoutes.some(route => 
                window.location.hostname.includes(`${route.trim()}.`) && window.location.hostname === publicAppUrl.hostname
            );
            
            // Redirect to maintenance if citizen maintenance is active and it's a public route
            if (isCitizenMaintenance && isPublicRoute && to.name !== 'maintenance') {
                next({ name: 'maintenance' });
                return;
            }
            
            // Allow access to maintenance page during maintenance
            if (to.name === 'maintenance' && isCitizenMaintenance) {
                next();
                return;
            }
            
            // Redirect away from maintenance page if not in maintenance mode
            if (to.name === 'maintenance' && !isCitizenMaintenance) {
                next({ name: 'home' });
                return;
            }

            // Auth check runs here (global beforeEach), NOT only in the
            // beforeRouteEnter mixin: Vue Router resolves the async route
            // component before in-component guards, so a protected page like
            // /application/:id would otherwise import its model and eagerly fire
            // auth-protected API calls (403) before the redirect happens.
            // Catching unauthenticated users here prevents that import entirely
            // and preserves the destination as returnUrl for post-login redirect.
            if (to.meta?.auth && from.path !== '/auth/signin') {
                await store.dispatch('auth/doWaitUntilInit');
                if (!store.getters['auth/isAuthenticated'] &&
                    !store.getters['auth/isAuthSucceeded']
                ) {
                    const returnUrl = to.fullPath.replace(/^\//, '');
                    next({ path: '/auth/signin', query: { returnUrl }, replace: true });
                    return;
                }
            }

            next();
        });

        router.afterEach(() => {
            ProgressBar.done();
        });
    }

    return router;
};

export { setupComponentsFiltersDirectivesAndMixins, routerAsync, storeAsync, previousRoute };
